import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./App.css";

import { enableWakeLockBestEffort } from "./utils/wakeLock";
import { recomputeViewport } from "./utils/viewport";
import { useSocket } from "./context/SocketContext";

import Landing from "./pages/Landing";
import Nickname from "./pages/Nickname";
import DragGame from "./pages/DragGame";
import Wire from "./pages/Wire";
import Score from "./pages/Score";
import EmptyPage from "./pages/EmptyPage";

/**
 * The whole flow is driven by the live backend session status —
 * TouchDesigner controls it via StartContent1/StartContent2/EndContents.
 * The only user-initiated step is Landing -> Nickname (the START button).
 *
 *   bootstrapping            -> loading (empty screen)
 *   no session / ENDED       -> EmptyPage
 *   CONTENTS_ENDED           -> Score
 *   active & not registered  -> Landing (then Nickname after START)
 *   active & registered:
 *       READY                -> DragGame  (joystick / OX positioning —
 *                                          shown immediately after register)
 *       CONTENT1 / CONTENT2  -> Wire      (tug-of-war tap, team red/green;
 *                                          the two 줄다리기 rounds)
 */
function App() {
  const { sessionStatus, sessionTerminated, bootstrapping, userInfo } = useSocket();

  useEffect(() => { enableWakeLockBestEffort(); }, []);

  const [started, setStarted] = useState(() => {
    try { return sessionStorage.getItem("museumx_started") === "1"; } catch { return false; }
  });
  const handleStart = () => {
    try { sessionStorage.setItem("museumx_started", "1"); } catch {}
    setStarted(true);
  };

  const norm = (sessionStatus || "").toString().toUpperCase();
  const hasSession = !!norm && norm !== "ENDED" && norm !== "NONE";
  const isRegistered = !!userInfo.nickname;

  let screen;
  if (bootstrapping) screen = "loading";
  // Sticky: once the session that this tab joined was hard-ended, stay on
  // EmptyPage even if a brand-new session starts. A reload (which re-runs
  // the QR Gate against the new session UUID) is the only way back in.
  else if (sessionTerminated) screen = "empty";
  else if (!hasSession) screen = "empty";
  else if (norm === "CONTENTS_ENDED") screen = "score";
  else if (!isRegistered) screen = started ? "nickname" : "landing";
  else if (norm === "CONTENT1" || norm === "CONTENT2") screen = "wire";
  else screen = "drag"; // registered + READY -> joystick / OX positioning

  // iOS: drop focus / block undo gestures when the screen changes.
  // The Nickname screen is exempt — its input is focused on purpose.
  useEffect(() => {
    const onBeforeInput = (e) => {
      if (e.inputType === "historyUndo" || e.inputType === "historyRedo") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("beforeinput", onBeforeInput, true);

    // iOS "Shake to Undo" fires `undo`/`redo` events — swallow them so the
    // system popup never has anything to act on.
    const onUndoRedo = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener("undo", onUndoRedo, true);
    document.addEventListener("redo", onUndoRedo, true);

    let timer;
    if (screen !== "nickname") {
      const blur = () => {
        try {
          const el = document.activeElement;
          if (el && typeof el.blur === "function") el.blur();
        } catch {}
        try { window.getSelection?.()?.removeAllRanges?.(); } catch {}
      };
      blur();
      timer = window.setTimeout(blur, 80);
    }

    // Fix D/F — force a viewport-vars recompute on every screen change.
    // After programmatic blur or a framer-motion-driven unmount, iOS does
    // not reliably fire visualViewport.resize, so --app-height can stay
    // stuck at a keyboard-up value (manifests as the Landing logo collapsed
    // into the upper portion of the screen). recomputeViewport() samples
    // now + next animation frame + +250ms, catching every realistic timing.
    recomputeViewport();

    return () => {
      document.removeEventListener("beforeinput", onBeforeInput, true);
      document.removeEventListener("undo", onUndoRedo, true);
      document.removeEventListener("redo", onUndoRedo, true);
      if (timer) window.clearTimeout(timer);
    };
  }, [screen]);

  let content;
  switch (screen) {
    case "landing": content = <Landing onStart={handleStart} />; break;
    case "nickname": content = <Nickname />; break;
    case "drag": content = <DragGame />; break;
    case "wire": content = <Wire />; break;
    case "score": content = <Score />; break;
    default: content = <EmptyPage />; break; // loading | empty
  }

  return (
    <div className="app-root">
      <AnimatePresence mode="wait">
        <motion.div
          key={screen}
          className="screen-fade"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {content}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default App;
